import React, { memo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner, FaCircleCheck, FaCircleXmark, FaClock } from 'react-icons/fa6';
import { CurrentProgress, TrainingMetrics, BatchProgress } from './types';
import { formatTime, getPostTrainingStepLabel } from './utils';

interface CurrentProgressSectionProps {
  currentProgress: CurrentProgress;
  currentPhase: string;
  epochProgress: number;
  latestMetrics?: TrainingMetrics | null;
  batchProgress?: BatchProgress | null;
}

// Status-based style maps (explicit classes for Tailwind JIT)
const containerStyles: Record<string, string> = {
  training_completed: 'border-emerald-200/60 bg-card',
  training_failed: 'border-red-200/60 bg-card',
  post_training: 'border-amber-200/60 bg-card',
  default: 'border-border bg-card',
};

const progressBarTrackStyles: Record<string, string> = {
  training_completed: 'bg-emerald-100',
  training_failed: 'bg-red-100',
  post_training: 'bg-amber-100',
  default: 'bg-primary/10',
};

const progressBarStyles: Record<string, string> = {
  training_completed: 'bg-emerald-500',
  training_failed: 'bg-red-500',
  post_training: 'bg-amber-500',
  default: 'bg-primary',
};

const phaseTextStyles: Record<string, string> = {
  training_completed: 'text-emerald-700',
  training_failed: 'text-red-700',
  post_training: 'text-amber-700',
  default: 'text-foreground',
};

const percentTextStyles: Record<string, string> = {
  training_completed: 'text-emerald-600',
  training_failed: 'text-red-600',
  post_training: 'text-amber-600',
  default: 'text-primary',
};

const statusIconMap: Record<string, React.ReactNode> = {
  training_completed: <FaCircleCheck className="text-emerald-500 text-sm" />,
  training_failed: <FaCircleXmark className="text-red-500 text-sm" />,
  post_training: <FaSpinner className="animate-spin text-amber-500 text-sm" />,
  default: <FaSpinner className="animate-spin text-primary text-sm" />,
};

export const CurrentProgressSection: React.FC<CurrentProgressSectionProps> = memo(({
  currentProgress,
  currentPhase,
  epochProgress,
  latestMetrics,
  batchProgress
}) => {
  const { t } = useTranslation('training');
  const prevStepRef = useRef<string | undefined>(currentProgress.step);

  // Live elapsed timer for post-training steps
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const stepStartTimeRef = useRef<number>(Date.now());
  const lastStepRef = useRef<string | undefined>(currentProgress.step);

  // Reset timer when step changes
  useEffect(() => {
    if (currentProgress.step !== lastStepRef.current) {
      stepStartTimeRef.current = Date.now();
      setElapsedSeconds(0);
      lastStepRef.current = currentProgress.step;
      prevStepRef.current = currentProgress.step;
    }
  }, [currentProgress.step]);

  // Update elapsed time every second during post-training
  useEffect(() => {
    if (currentProgress.status !== 'post_training') return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - stepStartTimeRef.current) / 1000);
      setElapsedSeconds(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentProgress.status, currentProgress.step]);

  const status = currentProgress.status;
  const containerClass = containerStyles[status] || containerStyles.default;
  const trackClass = progressBarTrackStyles[status] || progressBarTrackStyles.default;
  const barClass = progressBarStyles[status] || progressBarStyles.default;
  const phaseClass = phaseTextStyles[status] || phaseTextStyles.default;
  const percentClass = percentTextStyles[status] || percentTextStyles.default;
  const statusIcon = statusIconMap[status] || statusIconMap.default;

  const isPostTraining = status === 'post_training' || status === 'training_completed';
  const hasLiveMetrics = !isPostTraining && latestMetrics && (latestMetrics.epoch || latestMetrics.loss !== undefined);
  const hasBatchProgress = !isPostTraining && batchProgress && batchProgress.batch !== undefined;

  return (
    <div className={`rounded-xl border shadow-sm overflow-hidden transition-all duration-300 ${containerClass}`}>
      {/* Header */}
      <div className="px-4 py-3 sm:px-5 sm:py-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex-shrink-0">
            {statusIcon}
          </div>
          <span className={`text-xs sm:text-sm font-semibold tracking-tight truncate ${phaseClass}`}>
            {currentPhase}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {currentProgress.eta_seconds !== undefined && currentProgress.eta_seconds > 0 && !latestMetrics?.eta_formatted && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground font-mono tabular-nums">
              <FaClock className="text-[8px]" />
              {formatTime(currentProgress.eta_seconds)}
            </span>
          )}
          {latestMetrics?.eta_formatted && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground font-mono tabular-nums">
              <FaClock className="text-[8px]" />
              {latestMetrics.eta_formatted}
            </span>
          )}
          <span className={`text-xs sm:text-sm font-semibold tabular-nums ${percentClass}`}>
            {epochProgress}%
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-4 sm:px-5 pb-3 sm:pb-3.5">
        <div
          className={`w-full rounded-full h-2 overflow-hidden ${trackClass}`}
          role="progressbar"
          aria-valuenow={Math.round(Number(epochProgress) || 0)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('training.progress.barLabel', 'Training progress')}
        >
          <div
            className={`h-2 rounded-full transition-all duration-500 ease-out ${barClass} ${
              status === 'post_training' ? 'animate-pulse' : ''
            }`}
            style={{ width: `${epochProgress}%` }}
          />
        </div>
      </div>

      {/* Message */}
      {!hasLiveMetrics && currentProgress.message && (
        <div className="px-4 sm:px-5 pb-3">
          <p className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed">{currentProgress.message}</p>
        </div>
      )}

      {/* Post-Training Step Indicator */}
      {status === 'post_training' && currentProgress.step && (
        <div className="mx-4 sm:mx-5 mb-3 py-2 px-3 bg-amber-50/60 rounded-lg border border-amber-200/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[11px] sm:text-xs text-amber-700">
              <FaSpinner className="animate-spin text-amber-500 text-[10px] flex-shrink-0" />
              <span className="font-medium">
                {getPostTrainingStepLabel(currentProgress.step, t)}
              </span>
            </div>
            <span className="text-[10px] text-amber-500 font-mono tabular-nums flex-shrink-0">
              {formatTime(elapsedSeconds)}
            </span>
          </div>

          {currentProgress.details && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[10px] text-amber-600/70">
              {currentProgress.details.file_size_mb !== undefined && (
                <span>{currentProgress.details.file_size_mb.toFixed(1)} MB</span>
              )}
              {currentProgress.details.compression_ratio !== undefined && (
                <span>{currentProgress.details.compression_ratio.toFixed(0)}% smaller</span>
              )}
              {currentProgress.details.original_size_mb !== undefined &&
               currentProgress.details.compressed_size_mb !== undefined && (
                <span className="text-amber-500/60">
                  ({currentProgress.details.original_size_mb.toFixed(1)} → {currentProgress.details.compressed_size_mb.toFixed(1)} MB)
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Inline Training Metrics */}
      {hasLiveMetrics && (
        <div className="mx-4 sm:mx-5 mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 py-2 px-3 bg-muted/80 rounded-lg border border-border">
          {latestMetrics.epoch !== undefined && latestMetrics.total_epochs !== undefined && (
            <span className="text-[10px] sm:text-[11px] font-medium text-foreground">
              {t('postTraining.metrics.epoch')}: <span className="tabular-nums">{latestMetrics.epoch}/{latestMetrics.total_epochs}</span>
            </span>
          )}
          {latestMetrics.loss !== undefined && (
            <span className="text-[10px] sm:text-[11px] text-muted-foreground">
              {t('postTraining.metrics.loss')}: <span className="font-mono tabular-nums font-medium text-foreground">{latestMetrics.loss.toFixed(4)}</span>
            </span>
          )}
          {latestMetrics.val_loss !== undefined && (
            <span className="text-[10px] sm:text-[11px] text-muted-foreground">
              {t('postTraining.metrics.valLoss')}: <span className="font-mono tabular-nums font-medium text-foreground">{latestMetrics.val_loss.toFixed(4)}</span>
            </span>
          )}
          {latestMetrics.eta_formatted && (
            <span className="text-[10px] sm:text-[11px] font-medium text-primary">
              {t('postTraining.metrics.eta')}: {latestMetrics.eta_formatted}
            </span>
          )}
        </div>
      )}

      {/* Inline Batch Progress */}
      {hasBatchProgress && (
        <div className="mx-4 sm:mx-5 mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 py-2 px-3 bg-accent/50 rounded-lg border border-primary/10">
          <span className="text-[10px] sm:text-[11px] font-medium text-accent-foreground">{batchProgress.model_name}</span>
          <span className="text-[10px] sm:text-[11px] text-primary">
            {t('postTraining.metrics.batch')}: <span className="tabular-nums">{batchProgress.batch}/{batchProgress.total_batches}</span>
          </span>
          <span className="text-[10px] sm:text-[11px] text-primary">
            {t('postTraining.metrics.loss')}: <span className="font-mono tabular-nums font-medium text-primary">{batchProgress.loss.toFixed(4)}</span>
          </span>
        </div>
      )}

      {/* Training Details Footer */}
      {(currentProgress.models_to_train || currentProgress.models_trained !== undefined ||
        currentProgress.elapsed_seconds !== undefined || currentProgress.training_duration ||
        (currentProgress.estimated_duration && !currentProgress.eta_seconds && !latestMetrics?.eta_formatted)) && (
        <div className="px-4 sm:px-5 pb-3 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
          {currentProgress.models_to_train && (
            <span>{t('postTraining.metrics.models')}: {currentProgress.models_to_train.join(', ')}</span>
          )}
          {currentProgress.models_trained !== undefined && (
            <span>{t('postTraining.metrics.trained')}: {currentProgress.models_trained}</span>
          )}
          {currentProgress.elapsed_seconds !== undefined && (
            <span>{t('postTraining.metrics.elapsed')}: <span className="font-mono tabular-nums">{formatTime(currentProgress.elapsed_seconds)}</span></span>
          )}
          {currentProgress.eta_seconds !== undefined && currentProgress.eta_seconds > 0 && !latestMetrics?.eta_formatted && (
            <span className="sm:hidden font-medium text-primary">{t('postTraining.metrics.eta')}: {formatTime(currentProgress.eta_seconds)}</span>
          )}
          {currentProgress.training_duration && (
            <span>{t('postTraining.metrics.duration')}: <span className="font-mono tabular-nums">{currentProgress.training_duration.toFixed(1)}s</span></span>
          )}
          {currentProgress.estimated_duration && !currentProgress.eta_seconds && !latestMetrics?.eta_formatted && (
            <span>{t('postTraining.metrics.eta')}: {currentProgress.estimated_duration}</span>
          )}
        </div>
      )}
    </div>
  );
});

CurrentProgressSection.displayName = 'CurrentProgressSection';

export default CurrentProgressSection;
